import React, { useMemo, useState, useEffect } from 'react';
import { Wallet } from 'lucide-react';
import type { DailyData } from '../../types/index';
import { fmtMoney } from '../../utils/formatters';
import type { TimeRange } from '../../types/dashboard';
import {
  getDashboardWidgetTimeframe,
  getWidgetTimeframeTrailingDays,
  type WidgetTimeframeToken,
} from '../../lib/dashboardTimeRange';

import {
  extractTradeCommission,
  extractTradeCost,
  getTradePnl,
  getTradeTimestampMs,
  normalizeTradeStatus,
} from '../../lib/trades/authoritativeTradeData';

interface TotalCommissionsWidgetProps {
  dailyData?: DailyData[];
  trades?: any[];
  timeRange?: TimeRange;
}

export const TotalCommissionsWidget: React.FC<TotalCommissionsWidgetProps> = ({ trades = [], timeRange }) => {
  const [timeframe, setTimeframe] = useState<WidgetTimeframeToken>('7D');

  useEffect(() => {
    if (timeRange) setTimeframe(getDashboardWidgetTimeframe(timeRange));
  }, [timeRange]);

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (!detail || detail.id !== 'totalCommissions') return;
        const val = detail.value as WidgetTimeframeToken | string;
        if (val === '24H' || val === '7D' || val === '30D' || val === '12M' || val === 'YTD') setTimeframe(val);
      } catch {}
    };
    window.addEventListener('widget-timeframe:selected', handler as EventListener);
    return () => window.removeEventListener('widget-timeframe:selected', handler as EventListener);
  }, []);

  const filteredTrades = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return [];
    const now = Date.now();
    const tfDays = getWidgetTimeframeTrailingDays(timeframe);
    const tfMs = tfDays * 24 * 60 * 60 * 1000;
    const startOfYearMs = new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0).getTime();

    return trades.filter((t) => {
      const ts = getTradeTimestampMs(t);
      if (ts == null || !Number.isFinite(ts)) return false;
      if (timeframe === 'YTD') return ts >= startOfYearMs;
      return ts >= now - tfMs;
    });
  }, [trades, timeframe]);

  const { totalCommissions, grossPnl, netPnl } = useMemo(() => {
    // Canonical source of truth: costs are read from individual in-range trade
    // records, including open fills that have already incurred entry costs.
    // Realized net and gross P&L below remain closed-trade-only.
    let commissionsSum = 0;
    let closedCommissionsSum = 0;
    let net = 0;
    for (const trade of filteredTrades) {
      const cost = extractTradeCommission(trade);
      commissionsSum += cost;
      if (normalizeTradeStatus(trade) === 'closed') {
        // Gross P&L must add back every recorded execution cost (commission
        // plus fees), even though this card's headline is commission-only.
        closedCommissionsSum += extractTradeCost(trade);
        net += getTradePnl(trade);
      }
    }
    // Reported gross P&L reconstructs only realized trades; open-entry costs
    // are included in total commissions but not in realized gross P&L.
    const gross = net + closedCommissionsSum;
    return { totalCommissions: commissionsSum, grossPnl: gross, netPnl: net };
  }, [filteredTrades]);

  // Compute commission health and dispatch badge update for the card header
  const getCommissionHealth = (totalFees: number, grossPnlVal: number) => {
    if (totalFees < 0) {
      return { status: 'INCOME', color: 'green', description: 'Rebate Generated' };
    }
    if (grossPnlVal === 0) {
      return { status: 'N/A', color: 'gray' };
    }
    const dragRatio = (totalFees / Math.abs(grossPnlVal)) * 100;
    if (dragRatio < 10) return { status: 'HEALTHY', color: 'green', limit: 10 };
    if (dragRatio >= 10 && dragRatio < 30) return { status: 'MODERATE', color: 'blue', limit: 30 };
    if (dragRatio >= 30 && dragRatio < 50) return { status: 'OVER-TRADING', color: 'amber', limit: 50 };
    return { status: 'CHURNING', color: 'red', limit: 100 };
  };

  const dragPct = grossPnl === 0 ? 0 : Math.abs((totalCommissions / Math.abs(grossPnl)) * 100);

  useEffect(() => {
    try {
      const health = getCommissionHealth(totalCommissions, grossPnl);
      const ev = new CustomEvent('widget-badge:update', {
        detail: {
          id: 'totalCommissions',
          ...health,
          secondaryLabel: `(${dragPct.toFixed(1)}% Drag)`,
        },
      });
      window.dispatchEvent(ev);
    } catch {}
  }, [dragPct, totalCommissions, grossPnl]);

  // Progress bar widths (percent of gross)
  // netPortion was used for the progress bar; it's no longer needed so remove the variable
  // const netPortion = grossPnl === 0 ? 0 : Math.max(0, Math.min(100, (netPnl / grossPnl) * 100));

  return (
    <div className="relative flex h-full w-full flex-col px-0 pb-0 pt-6">
      {/* Badge moved to WidgetCard header for independent positioning */}

      <div className="flex min-h-0 flex-1 items-start justify-center">
        <span
          data-testid="total-commissions-amount"
          className="text-3xl font-bold leading-none text-zinc-700 dark:text-zinc-100"
        >
          {fmtMoney(totalCommissions)}
        </span>
      </div>

      <div
        data-testid="total-commissions-footer"
        className="mt-1 flex w-full flex-shrink-0 items-center justify-between gap-2 whitespace-nowrap text-[11px] leading-none text-zinc-600"
      >
        <div className="inline-flex min-w-0 items-center gap-1 text-zinc-200">
          <Wallet size={12} className="flex-shrink-0 text-zinc-800 dark:text-zinc-100" />
          <span>Net: <span className="font-medium text-zinc-800 dark:text-zinc-400">{fmtMoney(netPnl)}</span></span>
        </div>
        <div className="flex min-w-0 items-center gap-1">
          <span className="text-zinc-100">Gross:</span>
          <span className="font-medium text-zinc-800 dark:text-zinc-400">{fmtMoney(grossPnl)}</span>
        </div>
      </div>
    </div>
  );
};
